import type React from 'react';
import {
  Td,
  Tr,
  Chip,
  ContentCounter,
  StatusIcon,
  TextLink,
  colors,
  colorUtility,
  Flex,
  RiLinkM,
  RiSquareFill,
  Label,
  RiFilePaperLine,
  RiPencilLine
} from '@itandi/itandi-bb-ui';
import type { DealIndexClient } from '~/models/Deal';
import { PATH as CLIENT_DEAL_PATH } from '~/pages/deals/[id]';
import { DealTypeChip } from '~/components/common/parts/chip/DealTypeChip';
import { PATH as PROPERTY_DEAL_PATH } from '~/pages/property_deals/[id]/index';
import { truncate } from '~/utils/StringUtil';
import { statusColor } from '~/models/Status';
import { DateCell } from '~/components/common/deals/index/DateCell';
import { DealInspectionDateCell } from '~/components/common/deals/index/DealInspectionDateCell';

type Props = {
  deal: DealIndexClient;
  statusNames: { [p: string]: string };
};

export const DealTableRow: React.FC<Props> = ({ deal, statusNames }: Props) => {
  const unread = deal.unreadMessageCount > 0 || deal.displayUnread;
  const unreadChipLabel =
    deal.unreadMessageCount > 0 ? `未読${deal.unreadMessageCount}` : '未読';
  return (
    <Tr>
      <Td textAlign="Left" verticalAlign="Top">
        <Flex alignItems="flex-start" direction="column" gap={2}>
          <Chip
            Icon={RiSquareFill}
            iconColor={statusColor[deal.status]}
            label={statusNames[deal.status]}
          />
          {unread && (
            <Chip
              background={colors['semantic-negative']}
              borderColor={null}
              label={unreadChipLabel}
              textColor={colors['itandi-white']}
            />
          )}
          {deal.hasTemporaryDealCancel && (
            <Chip
              background={colors['semantic-negative']}
              borderColor={null}
              Icon={RiFilePaperLine}
              iconColor={colors['itandi-white']}
              label="取下申請中"
              textColor={colors['itandi-white']}
            />
          )}
          {deal.hasRequestEstimation && (
            <Chip
              background={colors['expressive-aqua-dark']}
              borderColor={null}
              Icon={RiFilePaperLine}
              iconColor={colors['itandi-white']}
              label="見積申請あり"
              textColor={colors['itandi-white']}
            />
          )}
          {deal.hasDraftEstimationByCompany && (
            <Chip
              background={colors['expressive-yellow-dark']}
              borderColor={null}
              Icon={RiPencilLine}
              iconColor={colors['itandi-white']}
              label="見積編集中"
              textColor={colors['itandi-white']}
            />
          )}
          {deal.hasRequestEstimationByCompany && (
            <Chip
              background={colors['expressive-blue']}
              borderColor={null}
              Icon={RiFilePaperLine}
              iconColor={colors['itandi-white']}
              label="見積申請中"
              textColor={colors['itandi-white']}
            />
          )}
        </Flex>
      </Td>
      <Td textAlign="Left" verticalAlign="Top">
        <Flex gap={2}>
          <DealTypeChip
            dealSubtype={deal.dealSubtype}
            dealType={deal.dealType}
          />
          {deal.relatedDealCount > 0 && (
            <Chip
              background={colorUtility(colors['expressive-aqua-dark']).alpha(
                0.1
              )}
              borderColor={colors['expressive-aqua-dark']}
              fontWeight="bold"
              Icon={RiLinkM}
              iconColor={colors['expressive-aqua-dark']}
              label={`関連${deal.relatedDealCount}`}
              onClick={(): void => {
                window.open(PROPERTY_DEAL_PATH(deal.propertyDealId));
              }}
              textColor={colors['expressive-aqua-dark']}
            />
          )}
        </Flex>
        <Flex direction="column" gap={2}>
          <TextLink
            href={CLIENT_DEAL_PATH(deal.id)}
            target="_blank"
            variant="PrimaryBold"
          >
            {`${truncate(deal.name, 30)} ${deal.roomNumber || ''}`}
          </TextLink>
          <Label color="subtle">{truncate(deal.address, 35)}</Label>
          {deal.tenantName && (
            <Label color="subtle">
              {`${truncate(deal.tenantName, 15)} 様`}
            </Label>
          )}
        </Flex>
      </Td>
      <Td textAlign="Left" verticalAlign="Top">
        {deal.uncompletedTodoCount > 0 && (
          <ContentCounter
            count={deal.uncompletedTodoCount}
            onClick={(): void => {
              window.open(CLIENT_DEAL_PATH(deal.id));
            }}
            variant="Todo"
          />
        )}
      </Td>
      <Td textAlign="Left" verticalAlign="Top">
        <Label>{deal.companyStoreSetting.name}</Label>
        <br />
        <Label color="subtle" size="xs">
          {truncate(deal.staff.name, 35)}
        </Label>
      </Td>
      <Td textAlign="Left" verticalAlign="Top">
        {deal.dealVendorCompanies.length === 0 ? (
          <>
            <Label>施工会社未指定</Label>
            <StatusIcon variant="Alert" />
          </>
        ) : (
          <Flex direction="column" gap={2}>
            <Label>
              {deal.dealVendorCompanies[0].companyVendor?.name ?? ''}
            </Label>
            {deal.dealVendorCompanies[0].user ? (
              <Label color="subtle" size="xs">
                {truncate(deal.dealVendorCompanies[0].user.name, 35)}
              </Label>
            ) : (
              deal.dealVendorCompanies[0].vendorCompany && (
                <Flex>
                  <Label size="xs">担当者設定なし</Label>
                  <StatusIcon variant="Alert" />
                </Flex>
              )
            )}
          </Flex>
        )}
      </Td>
      <DateCell date={deal.updatedDate} displayTime />
      <DateCell date={deal.terminationDate} />
      <DealInspectionDateCell deal={deal} />
      <DateCell date={deal.surveyDate} />
      <DateCell date={deal.sendEstimateDate} />
      <DateCell date={deal.orderDate} />
      <DateCell date={deal.constructionStartDate} />
      <DateCell date={deal.constructionEndDate} />
      <DateCell date={deal.constructionReportedDate} />
      <DateCell date={deal.completedDate} />
      <DateCell date={deal.preferredDate} />
      <DateCell date={deal.createdAt} />
    </Tr>
  );
};
